/**
 * Reading a bank statement line: what is noise, who was paid, and what that
 * payee usually is.
 *
 * Categorising an import used to be one thing -- a model, asked about every
 * row, with nothing remembered afterwards. It filed Allegro under groceries,
 * a transfer to the person's own Revolut under the car, and the same shop
 * under two categories in two imports. Now four things answer before a model
 * is asked, in this order:
 *
 *   1. the category a row already carries (this app's own export),
 *   2. a rule the account made itself by correcting a row on the review screen,
 *   3. a phrase that means money moved between the person's own accounts --
 *      not spending at all, so the row is left out of the import,
 *   4. a dictionary of Polish merchants and statement phrases.
 *
 * The model only sees what none of them settled, and sees it cleaned: no card
 * numbers, dates or payment boilerplate, one line per distinct payee.
 */

import { CATEGORIES } from './model.js';

/**
 * Not a category: money moved between the person's own accounts. Never stored
 * -- a row marked this way is left out of the import instead.
 */
export const TRANSFER = 'Transfer';

const LETTERS = {
  ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z'
};

/** Lower case, no diacritics, words separated by single spaces. */
export function fold(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, letter => LETTERS[letter])
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    // McDonald's is one word, not "mcdonald s".
    .replace(/['’‘`´]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/*
 * What banks wrap around a payee: the payment method, the booking dates, the
 * card number's label, the country. Removed before anything is matched, and
 * longest first, so "nr karty" goes before a shorter phrase can split it.
 */
const NOISE = [
  'data transakcji', 'data waluty', 'data ksiegowania', 'data operacji', 'data zlecenia',
  'zakup przy uzyciu karty', 'platnosc karta', 'transakcja karta', 'transakcja kartowa',
  'operacja karta', 'zakup karta', 'platnosc blik', 'zakup blik', 'transakcja blik',
  'platnosc web blik', 'platnosc internetowa', 'platnosc mobilna', 'google pay', 'apple pay',
  'garmin pay', 'nr karty', 'numer karty', 'karta nr', 'nr transakcji', 'numer transakcji',
  'nr ref', 'oryginalna kwota operacji', 'kwota oryginalna', 'kwota', 'kurs',
  'tytul', 'lokalizacja', 'adres', 'miasto', 'kraj', 'odbiorca', 'nadawca',
  'nazwa odbiorcy', 'nazwa nadawcy', 'rachunek odbiorcy', 'rachunek nadawcy', 'nr rachunku',
  'sp z o o', 'spolka z o o', 'sp zoo', 'sp j', 'sp k', 's a', 'spolka akcyjna',
  'blik', 'ref', 'polska', 'poland', 'pol', 'pl', 'pln', 'eur', 'usd', 'gbp', 'chf'
].sort((a, b) => b.length - a.length);

function stripNoise(folded) {
  let text = ' ' + folded + ' ';
  for (const phrase of NOISE) {
    const needle = ' ' + phrase + ' ';
    while (text.includes(needle)) text = text.replace(needle, ' ');
  }
  return text.replace(/ +/g, ' ').trim();
}

/** A token that is a number, a date part, a terminal code or a masked card number. */
const isCode = word => /^\d+$/.test(word) || /\d{3,}/.test(word) || /^x+$/.test(word);

/**
 * The description as a model should see it: the words that say who was paid
 * and for what, without the codes around them. Falls back to the original when
 * cleaning leaves nothing, so a line is never sent as an empty string.
 */
export function cleanDescription(text) {
  const kept = stripNoise(fold(text)).split(' ').filter(word => word && !isCode(word));
  return kept.join(' ') || String(text ?? '').trim();
}

/*
 * Words that describe the payment rather than the payee. Left out of a payee's
 * key, or every phone transfer would share one: "przelew na telefon do Jana"
 * and "przelew na telefon do Ewy" are two different people.
 */
const NOT_A_PAYEE = new Set([
  'przelew', 'przelewy', 'przelewem', 'na', 'do', 'od', 'dla', 'za', 'po', 'przy', 'nr', 'numer',
  'telefon', 'zakup', 'zakupy',
  'platnosc', 'platnosci', 'transakcja', 'operacja', 'wyplata', 'wplata', 'zlecenie', 'stale',
  'polecenie', 'zaplaty', 'uznanie', 'obciazenie', 'karta', 'kartowa', 'internetowa', 'mobilna',
  'krajowy', 'zagraniczny', 'przychodzacy', 'wychodzacy', 'natychmiastowy', 'express', 'elixir',
  'sorbnet', 'payu', 'przelewy24', 'dotpay', 'paynow', 'tpay', 'autopay', 'paypro', 'paypal',
  'www', 'com', 'sklep', 'internetowy'
]);

/**
 * The same payee, however the bank decorated it this time: up to four words,
 * no numbers, no payment boilerplate. "ZABKA Z6655 K.1 WARSZAWA" and
 * "Płatność kartą 12.09 ZABKA Z6655 WARSZAWA" are both "zabka warszawa".
 * Empty when nothing identifying is left, and then no rule is kept for it.
 */
export function merchantKey(text) {
  return stripNoise(fold(text))
    .split(' ')
    .filter(word => word.length > 1 && !/\d/.test(word) && !/^x+$/.test(word) && !NOT_A_PAYEE.has(word))
    .slice(0, 4)
    .join(' ');
}

/**
 * Who was paid, for an imported row. This app's own export writes the
 * category as the description and the shop as the note, so there the note is
 * the payee.
 */
export function payeeOf(entry) {
  const label = String(entry?.label ?? '');
  return CATEGORIES.includes(label) ? String(entry?.note ?? '') : label;
}

// ------------------------------------------------------------ transfers ----

/*
 * Money moved between the person's own accounts: a transfer to savings, a
 * deposit, topping up a Revolut, funding a brokerage account. None of it is
 * spending, and counting it as spending doubled every month it happened in.
 */
const TRANSFER_PHRASES = [
  'przelew wlasny', 'przelew na rachunek wlasny', 'przelew na konto wlasne',
  'przelew miedzy rachunkami', 'przelew miedzy kontami', 'przelew miedzy wlasnymi rachunkami',
  'przelew wewnetrzny', 'przeniesienie srodkow', 'przeksiegowanie', 'zasilenie rachunku',
  'zasilenie konta', 'zasilenie', 'doladowanie konta', 'doladowanie rachunku',
  'doladowanie revolut', 'top up', 'topup', 'revolut', 'zen com', 'transferwise', 'wise com',
  'konto oszczednosciowe', 'rachunek oszczednosciowy', 'cel oszczednosciowy', 'skarbonka',
  'lokata', 'lokaty', 'na lokate', 'xtb', 'x trade brokers', 'dm bos', 'bossa', 'own transfer',
  'transfer between accounts'
];

/* Interest on a deposit mentions the deposit, and is income, not a transfer. */
const NOT_A_TRANSFER = ['odsetki', 'kapitalizacja', 'oprocentowanie', 'interest'];

const hasPhrase = (text, phrase) => (' ' + text + ' ').includes(' ' + phrase + ' ');

const TRANSFERS_LONGEST_FIRST = [...TRANSFER_PHRASES].sort((a, b) => b.length - a.length);

/** The transfer phrase a folded description contains, or null. */
function transferMatch(folded) {
  if (NOT_A_TRANSFER.some(word => hasPhrase(folded, word))) return null;
  return TRANSFERS_LONGEST_FIRST.find(phrase => hasPhrase(folded, phrase)) || null;
}

export function isOwnTransfer(text) {
  return transferMatch(stripNoise(fold(text))) !== null;
}

// ----------------------------------------------------------- dictionary ----

/*
 * Polish merchants and statement phrases, by the category they almost always
 * mean. Matched as whole words, and the longest match wins -- so "uber eats"
 * beats "uber", "google play" beats "play", "orlen paczka" beats "orlen" --
 * whatever the order below.
 */
const DICTIONARY = {
  Housing: [
    'czynsz', 'najem', 'wynajem', 'wspolnota mieszkaniowa', 'wspolnota', 'spoldzielnia mieszkaniowa',
    'spoldzielnia', 'zarzadca nieruchomosci', 'zarzad nieruchomosci', 'administracja nieruchomosci',
    'tbs', 'zgm', 'zarzad budynkow', 'pge', 'pge obrot', 'tauron', 'enea', 'energa', 'innogy',
    'e on', 'eon', 'stoen', 'fortum', 'veolia', 'pgnig', 'polska spolka gazownictwa', 'psg',
    'gazownia', 'gaz ziemny', 'mpwik', 'wodociagi', 'wod kan', 'wodkan', 'aquanet', 'mpec',
    'cieplownia', 'energia elektryczna', 'wywoz smieci', 'odpady', 'oplata za odpady',
    'gospodarowanie odpadami', 'smieci',
    /* American chains: the statements Product Hunt brings. */
    'rent payment', 'mortgage payment', 'hoa dues', 'property management'
  ],
  Groceries: [
    'biedronka', 'jeronimo martins', 'lidl', 'zabka', 'zabka nano', 'kaufland', 'auchan',
    'carrefour', 'carrefour express', 'netto', 'dino', 'stokrotka', 'lewiatan', 'polomarket',
    'intermarche', 'aldi', 'spolem', 'freshmarket', 'fresh market', 'delikatesy centrum',
    'delikatesy', 'topaz', 'chata polska', 'eurospar', 'spar', 'frisco', 'piotr i pawel',
    'e leclerc', 'leclerc', 'selgros', 'makro', 'groszek', 'odido', 'twoj market', 'arhelan',
    'hala targowa', 'targowisko', 'bazar', 'piekarnia', 'cukiernia', 'warzywniak',
    'sklep spozywczy', 'supermarket', 'hipermarket', 'minimarket', 'masarnia', 'rzeznik',
    'dzien dobry', 'globi', 'sezam', 'gram market', 'ale market', 'eurosklep', 'euro sklep',
    /* American chains: the statements Product Hunt brings. */
    'whole foods', 'whole foods market', 'trader joes', 'kroger', 'safeway', 'publix', 'costco', 'heb', 'h e b', 'wegmans', 'albertsons', 'food lion', 'giant eagle', 'sprouts', 'meijer', 'harris teeter', 'stop shop', 'instacart', 'vons', 'ralphs', 'winco', 'grocery outlet', 'hy vee', 'shoprite', 'piggly wiggly', 'walmart grocery', 'amazon fresh'
  ],
  Dining: [
    'uber eats', 'ubereats', 'bolt food', 'glovo', 'wolt', 'pyszne', 'pyszne pl', 'takeaway',
    'mcdonalds', 'mc donalds', 'kfc', 'burger king', 'max premium burgers', 'max burgers',
    'subway', 'pizza hut', 'dominos', 'telepizza', 'da grasso', 'pizzeria', 'pizza',
    'restauracja', 'restaurant', 'ristorante', 'trattoria', 'bistro', 'bar', 'bar mleczny', 'pub',
    'kawiarnia', 'cafe', 'caffe', 'coffee', 'kawa', 'kawka', 'starbucks', 'costa coffee', 'costa',
    'green caffe nero', 'caffe nero', 'kebab', 'kebap', 'sushi', 'ramen', 'pierogarnia',
    'stolowka', 'kantyna', 'food truck', 'lody', 'lodziarnia', 'gelateria', 'amrest',
    'north fish', 'sphinx', 'sfinks', 'pasibus', 'bobby burger', 'manekin', 'zapiekanki',
    'grill', 'piwiarnia', 'winiarnia', 'browar', 'catering', 'obiady', 'jadlodajnia',
    /* American chains: the statements Product Hunt brings. */
    'chipotle', 'chick fil a', 'taco bell', 'wendys', 'panera', 'dunkin', 'doordash', 'grubhub', 'postmates', 'five guys', 'shake shack', 'in n out', 'sweetgreen', 'olive garden', 'applebees', 'chilis', 'panda express', 'popeyes', 'arbys', 'sonic drive in', 'dutch bros', 'tim hortons', 'jack in the box', 'wingstop', 'jersey mikes', 'jimmy johns', 'cava'
  ],
  Shopping: [
    'allegro', 'allegro pl', 'amazon', 'aliexpress', 'temu', 'shein', 'zalando', 'empik',
    'media expert', 'mediaexpert', 'rtv euro agd', 'euro rtv agd', 'x kom', 'morele',
    'komputronik', 'neonet', 'media markt', 'mediamarkt', 'saturn', 'decathlon', 'ikea',
    'castorama', 'leroy merlin', 'obi', 'bricomarche', 'psb mrowka', 'mrowka', 'jysk',
    'agata meble', 'black red white', 'pepco', 'action', 'sinsay', 'reserved', 'cropp',
    'mohito', 'lpp', 'ccc', 'deichmann', 'eobuwie', 'modivo', 'h m', 'hm', 'zara', 'bershka',
    'pull bear', 'stradivarius', 'primark', 'tk maxx', 'tkmaxx', 'kik', 'tedi', 'martes sport',
    'go sport', 'intersport', '4f', 'big star', 'diverse', 'rossmann', 'hebe', 'douglas',
    'sephora', 'notino', 'inglot', 'drogeria', 'drogerie natura', 'smyk', 'vinted', 'olx',
    'ceneo', 'matras', 'swiat ksiazki', 'taniaksiazka', 'bonito', 'apart', 'w kruk',
    'apple store', 'trafika', 'kiosk', 'kiosk ruch', 'inmedio', 'relay', 'kolporter', 'inpost',
    'paczkomat', 'paczkomaty', 'orlen paczka', 'dpd', 'dhl', 'gls', 'poczta polska',
    'kwiaciarnia',
    /* American chains: the statements Product Hunt brings. */
    'target', 'walmart', 'best buy', 'home depot', 'lowes', 'macys', 'nordstrom', 'tj maxx', 'marshalls', 'ross stores', 'kohls', 'old navy', 'ebay', 'etsy', 'wayfair', 'apple store', 'dollar tree', 'dollar general', 'sams club', 'staples', 'michaels', 'ulta', 'sephora'
  ],
  Vehicle: [
    'orlen', 'pkn orlen', 'lotos', 'bp', 'shell', 'circle k', 'statoil', 'moya', 'amic',
    'amic energy', 'huzar', 'avia', 'stacja paliw', 'paliwo', 'lpg', 'myjnia', 'parking',
    'parkowanie', 'strefa platnego parkowania', 'spp', 'oplata parkingowa', 'e toll', 'etoll',
    'viatoll', 'autostrada', 'autostrada wielkopolska', 'stalexport', 'wulkanizacja',
    'autoserwis', 'serwis samochodowy', 'mechanik', 'warsztat samochodowy', 'inter cars',
    'auto czesci', 'autoczesci', 'norauto', 'stacja kontroli pojazdow', 'skp',
    'przeglad techniczny', 'greenway', 'ionity', 'orlen charge',
    /* American chains: the statements Product Hunt brings. */
    'chevron', 'exxon', 'exxonmobil', 'mobil', 'sunoco', 'valero', 'speedway', 'wawa', 'sheetz', 'quiktrip', 'racetrac', 'arco', 'costco gas', 'jiffy lube', 'autozone', 'oreilly auto', 'advance auto parts', 'pep boys', 'valvoline'
  ],
  Transport: [
    'pkp', 'pkp intercity', 'intercity', 'koleje mazowieckie', 'koleje slaskie',
    'koleje dolnoslaskie', 'koleje wielkopolskie', 'lodzka kolej aglomeracyjna', 'polregio',
    'przewozy regionalne', 'skm', 'wkd', 'koleo', 'ztm', 'zkm', 'mpk', 'mzk', 'kzk gop', 'zditm',
    'jakdojade', 'komunikacja miejska', 'bilet komunikacji', 'uber', 'bolt', 'free now',
    'freenow', 'itaxi', 'taxi', 'taksowka', 'flixbus', 'polski bus', 'ryanair', 'wizz air',
    'wizzair', 'lot polish airlines', 'polskie linie lotnicze', 'lufthansa', 'easyjet',
    'norwegian air', 'blablacar', 'veturilo', 'nextbike', 'lime', 'hulajnoga', 'traficar',
    'panek carsharing', 'carsharing',
    /* American chains: the statements Product Hunt brings. */
    'lyft', 'amtrak', 'mta', 'bart', 'delta air', 'united airlines', 'american airlines', 'southwest airlines', 'jetblue', 'ez pass', 'e zpass', 'sunpass', 'fastrak', 'greyhound'
  ],
  Utilities: [
    'orange', 'orange polska', 'orange flex', 't mobile', 'tmobile', 'play', 'p4', 'plus gsm',
    'polkomtel', 'heyah', 'nju mobile', 'nju', 'virgin mobile', 'lycamobile', 'netia', 'vectra',
    'upc', 'inea', 'multimedia polska', 'toya', 'cyfrowy polsat', 'polsat box',
    'doladowanie telefonu',
    /* American chains: the statements Product Hunt brings. */
    'verizon', 'at t', 't mobile', 'comcast', 'xfinity', 'spectrum', 'con edison', 'pg e', 'duke energy', 'national grid', 'cox communications'
  ],
  Subscriptions: [
    'netflix', 'spotify', 'hbo', 'hbo max', 'max com', 'disney', 'disney plus', 'youtube',
    'youtube premium', 'google youtube', 'google play', 'google one', 'google storage',
    'google cloud', 'google workspace', 'gsuite', 'google', 'apple com', 'apple com bill',
    'itunes', 'icloud', 'apple music', 'microsoft', 'msft', 'office 365', 'microsoft 365',
    'amazon prime', 'prime video', 'amazon web services', 'aws', 'audible', 'storytel',
    'legimi', 'empik go', 'bookbeat', 'tidal', 'deezer', 'player pl', 'polsat box go', 'canal',
    'canal plus', 'viaplay', 'skyshowtime', 'chatgpt', 'openai', 'anthropic', 'claude ai',
    'deepseek', 'midjourney', 'perplexity', 'github', 'gitlab', 'jetbrains', 'dropbox', 'adobe',
    'canva', 'figma', 'notion', 'obsidian', '1password', 'bitwarden', 'lastpass', 'nordvpn',
    'expressvpn', 'surfshark', 'proton', 'protonmail', 'ovh', 'ovhcloud', 'hostinger',
    'home pl', 'nazwa pl', 'cyberfolks', 'awhost', 'linuxpl', 'zenbox', 'hetzner',
    'digitalocean', 'vultr', 'linode', 'cloudflare', 'namecheap', 'godaddy', 'vps', 'hosting',
    'serwer vps', 'domena', 'tradingview', 'patreon', 'substack', 'linkedin', 'duolingo',
    'headspace', 'strava', 'tinder', 'twitch', 'discord', 'you tube', 'you tube premium', 'viki',
    'rakuten viki',
    /* American chains: the statements Product Hunt brings. */
    'hulu', 'peacock', 'paramount plus', 'apple com bill', 'google storage', 'youtube premium', 'audible', 'amazon prime', 'openai', 'chatgpt', 'dropbox', 'adobe', 'microsoft 365', 'icloud', 'patreon', 'siriusxm'
  ],
  Health: [
    'apteka', 'apteki', 'doz', 'dbam o zdrowie', 'super pharm', 'superpharm', 'ziko', 'cefarm',
    'luxmed', 'lux med', 'medicover', 'enel med', 'enelmed', 'centrum medyczne', 'przychodnia',
    'szpital', 'stomatolog', 'stomatologia', 'dentysta', 'dental', 'ortodonta',
    'gabinet lekarski', 'lekarz', 'laboratorium', 'diagnostyka', 'synevo', 'alab',
    'fizjoterapia', 'rehabilitacja', 'optyk', 'vision express', 'salony optyczne', 'psycholog',
    'psychoterapia', 'multisport', 'benefit systems', 'medicover sport', 'zdrofit', 'calypso',
    'fitness', 'silownia', 'pure jatomi', 'just gym', 'mcfit', 'gym', 'crossfit', 'joga', 'yoga',
    'medicoversport', 'dla zdrowia', 'centrum zdrowia',
    /* American chains: the statements Product Hunt brings. */
    'cvs', 'cvs pharmacy', 'walgreens', 'rite aid', 'quest diagnostics', 'labcorp', 'kaiser'
  ],
  Insurance: [
    'pzu', 'warta', 'ergo hestia', 'hestia', 'allianz', 'generali', 'uniqa', 'aviva',
    'nationale nederlanden', 'nn zycie', 'link4', 'compensa', 'wiener', 'proama', 'mtu',
    'benefia', 'trasti', 'balcia', 'axa', 'signal iduna', 'tuz', 'saltus', 'interrisk',
    'colonnade', 'polisa', 'polisy', 'ubezpieczenie', 'ubezpieczenia', 'ubezp',
    'towarzystwo ubezpieczen', 'skladka ubezpieczeniowa', 'nnw', 'nww',
    /* American chains: the statements Product Hunt brings. */
    'geico', 'state farm', 'progressive ins', 'allstate', 'liberty mutual', 'usaa', 'farmers insurance', 'nationwide', 'lemonade insurance'
  ],
  Leisure: [
    'kino', 'cinema city', 'multikino', 'helios', 'teatr', 'filharmonia', 'opera', 'muzeum',
    'koncert', 'ebilet', 'eventim', 'biletomat', 'steam', 'steampowered', 'playstation',
    'sony interactive', 'xbox', 'nintendo', 'epic games', 'gog com', 'g2a', 'instant gaming',
    'blizzard', 'riot games', 'ubisoft', 'basen', 'aquapark', 'park wodny', 'energylandia',
    'zoo', 'kregle', 'bowling', 'escape room', 'paintball', 'park trampolin', 'booking com',
    'booking', 'airbnb', 'hotel', 'hostel', 'pensjonat', 'apartamenty', 'trivago', 'itaka',
    'rainbow', 'tui', 'wakacje pl', 'coral travel', 'lotto', 'totalizator sportowy', 'fortuna',
    'sts', 'betclic', 'superbet', 'etoto', 'lvbet', 'wedkarski', 'hobby',
    /* American chains: the statements Product Hunt brings. */
    'amc theatres', 'regal cinemas', 'ticketmaster', 'stubhub', 'steam games', 'playstation', 'xbox', 'nintendo', 'planet fitness', 'la fitness', 'equinox', 'airbnb', 'marriott', 'hilton', 'expedia'
  ],
  Fees: [
    'prowizja', 'prowizje', 'oplata za prowadzenie rachunku', 'oplata za prowadzenie konta',
    'oplata za karte', 'oplata za przelew', 'oplata za wyplate', 'oplata za sms',
    'oplata miesieczna za karte', 'odsetki', 'urzad skarbowy', 'mikrorachunek',
    'mikrorachunek podatkowy', 'pit', 'vat', 'podatek', 'podatek od nieruchomosci', 'zus', 'krus',
    'urzad gminy', 'urzad miasta', 'urzad miejski', 'ug', 'um', 'starostwo',
    'wydzial komunikacji', 'sad rejonowy', 'sad okregowy', 'e sad', 'ekw', 'krs',
    'oplata skarbowa', 'oplata sadowa', 'notariusz', 'kancelaria notarialna', 'komornik',
    'mandat', 'gov pl', 'epuap', 'paszport', 'oplata administracyjna',
    /* American chains: the statements Product Hunt brings. */
    'atm fee', 'overdraft fee', 'monthly service fee', 'foreign transaction fee', 'interest charge', 'late fee', 'irs treas', 'us treasury'
  ],
  'Debt service': [
    'splata kredytu', 'splata raty', 'rata kredytu', 'rata pozyczki', 'splata pozyczki',
    'splata karty', 'splata karty kredytowej', 'splata zadluzenia', 'kredyt hipoteczny',
    'kredyt gotowkowy', 'kredyt', 'rata', 'provident', 'wonga', 'vivus', 'smartney',
    'santander consumer', 'allegro pay', 'allegropay', 'credit card', 'karta kredytowa',
    'revolut credit card'
  ]
};

/** Which categories the dictionary can answer with -- read by the tests. */
export const DICTIONARY_CATEGORIES = Object.keys(DICTIONARY);

/* Every phrase once, folded the same way a description is, longest first. */
const PHRASES = Object.entries(DICTIONARY)
  .flatMap(([category, phrases]) => phrases.map(phrase => ({ phrase: fold(phrase), category })))
  .sort((a, b) => b.phrase.length - a.phrase.length);

/** The longest dictionary phrase a folded description contains, with its category. */
function dictionaryMatch(folded) {
  return PHRASES.find(({ phrase }) => hasPhrase(folded, phrase)) || null;
}

/** The category the dictionary gives a description, or null when it does not know it. */
export function dictionaryCategory(text) {
  const found = dictionaryMatch(stripNoise(fold(text)));
  return found ? found.category : null;
}

// --------------------------------------------------------------- verdict ----

/**
 * What the importer can say about a row without asking anybody.
 *
 * @param entry  an imported row: { kind, label, note }
 * @param rules  Map of merchant key → category, from the account's own corrections
 * @returns {{ category: string|null, source: string|null, transfer: boolean }}
 *   source is 'file', 'rule', 'transfer', 'dictionary', or null for a row
 *   nothing recognised -- the only rows a model is asked about.
 */
export function classify(entry, rules = new Map()) {
  const label = String(entry?.label ?? '');
  const note = String(entry?.note ?? '');
  const text = [label, note].filter(Boolean).join(' ');

  if (entry?.kind !== 'expense') {
    /* Income has no categories, but a transfer in is not income either. */
    return isOwnTransfer(text)
      ? { category: null, source: 'transfer', transfer: true }
      : { category: null, source: null, transfer: false };
  }

  if (CATEGORIES.includes(label)) return { category: label, source: 'file', transfer: false };

  const key = merchantKey(payeeOf(entry));
  const learned = key ? rules.get(key) : null;
  if (learned && CATEGORIES.includes(learned)) return { category: learned, source: 'rule', transfer: false };

  /*
   * A transfer phrase says where the money went; a longer dictionary phrase
   * says why. "Revolut" alone is topping up the person's own account --
   * "Revolut Credit Card" is a card being paid off.
   */
  const folded = stripNoise(fold(text));
  const moved = transferMatch(folded);
  const known = dictionaryMatch(folded);
  if (moved && !(known && known.phrase.length > moved.length)) {
    return { category: null, source: 'transfer', transfer: true };
  }
  if (known) return { category: known.category, source: 'dictionary', transfer: false };

  /*
   * The bank's own category, last of all: Chase writes "Groceries" or "Food &
   * Drink" beside every row, mBank "Żywność i chemia domowa". Coarser than a
   * merchant -- so it only speaks when nothing above knew the payee.
   */
  const bank = bankCategory(entry?.bankCategory);
  if (bank) return { category: bank, source: 'bank', transfer: false };

  return { category: null, source: null, transfer: false };
}

/* A bank's category names, folded, and the category each means here. */
const BANK_CATEGORIES = [
  ['groceries', 'Groceries'], ['supermarkets', 'Groceries'], ['food drink', 'Dining'], ['restaurants', 'Dining'],
  ['dining', 'Dining'], ['fast food', 'Dining'], ['coffee shops', 'Dining'], ['gas fuel', 'Vehicle'], ['gas', 'Vehicle'],
  ['fuel', 'Vehicle'], ['automotive', 'Vehicle'], ['travel', 'Transport'], ['transportation', 'Transport'],
  ['shopping', 'Shopping'], ['merchandise', 'Shopping'], ['entertainment', 'Leisure'], ['recreation', 'Leisure'],
  ['health wellness', 'Health'], ['health', 'Health'], ['medical', 'Health'], ['bills utilities', 'Utilities'],
  ['utilities', 'Utilities'], ['home', 'Housing'], ['rent', 'Housing'], ['mortgage', 'Housing'],
  ['fees adjustments', 'Fees'], ['fees', 'Fees'], ['taxes', 'Fees'], ['insurance', 'Insurance'],
  ['subscriptions', 'Subscriptions'], ['streaming', 'Subscriptions'],
  ['zywnosc i chemia domowa', 'Groceries'], ['zywnosc', 'Groceries'], ['jedzenie poza domem', 'Dining'],
  ['restauracje', 'Dining'], ['paliwo', 'Vehicle'], ['samochod', 'Vehicle'], ['transport', 'Transport'],
  ['rozrywka', 'Leisure'], ['zdrowie i uroda', 'Health'], ['zdrowie', 'Health'], ['odziez i obuwie', 'Shopping'],
  ['zakupy', 'Shopping'], ['oplaty i odsetki', 'Fees'], ['podatki', 'Fees'], ['ubezpieczenia', 'Insurance'],
  ['mieszkanie', 'Housing'], ['czynsz', 'Housing'], ['rachunki', 'Utilities'], ['subskrypcje', 'Subscriptions']
];

/** The category a bank's own label maps to: the whole label first, then the longest name inside it. */
export function bankCategory(text) {
  const folded = fold(text);
  if (!folded) return null;
  const exact = BANK_CATEGORIES.find(([name]) => name === folded);
  if (exact) return exact[1];
  const inside = BANK_CATEGORIES.filter(([name]) => hasPhrase(folded, name)).sort((a, b) => b[0].length - a[0].length)[0];
  return inside ? inside[1] : null;
}
